import '@fontsource/barlow-condensed/600.css';
import { useQueryClient } from '@tanstack/react-query';
import * as htmlToImage from 'html-to-image';
import { FC, MouseEvent, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled, { createGlobalStyle } from 'styled-components';

import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useDidUpdate from '@/hooks/useDidUpdate';
import useToasts from '@/hooks/useToasts';
import Card, { ActionButton } from '@/pages/MLProject/components/Card';
import ColorField from '@/pages/SubtitlesGenerationProject/components/ColorField';
import NumericInput from '@/pages/SubtitlesGenerationProject/components/NumericInput';
import { MLProjectTranslation } from '@/types/resources';
import { api } from '@/utils/api';
import {
  MLProjectTranslationStatus,
  MLProjectTranslationVideoStatus,
} from '@/utils/enums';
import { handleFormErrors } from '@/utils/helpers';
import srt from '@/utils/srt';

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Box = styled.div`
  padding: 24px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
`;

const Row = styled.div`
  display: flex;
  gap: 24px;
  > div {
    flex: 1;
    max-width: calc(50% - 12px);
  }
`;

const BgRow = styled(Row)`
  padding: 24px 24px 0;
  margin: 24px -24px 0;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
`;

const StyleButton = styled(ActionButton)`
  flex: 1;
  height: 70px;
`;

const ButtonsRow = styled.div`
  display: flex;
  gap: 16px;
`;

const ActionCard = styled(Box)`
  padding: 16px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`;

const ActionCardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const PreviewBox = styled.div`
  position: relative;
  width: 100%;
  padding-top: 288px;
  background-color: ${({ theme }) => theme.colors.grayscaleLight};
`;

const SubtitlesBox = styled.div`
  position: absolute;
  bottom: 0;
  top: 0;
  left: 0;
  right: 0;
  margin: auto;
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface SubtitlesTextProps {
  $bg: string;
  $color: string;
  $opacity: string;
  $paddingX: string;
  $paddingY: string;
  $fontSize: string;
}

export const SubtitlesTextStyles = createGlobalStyle<SubtitlesTextProps>`
.ml-subtitle-image {
  display: inline-block;
  font-family: 'Barlow Condensed', sans-serif;
  font-weight: 600;
  font-size: ${({ $fontSize }) => $fontSize}px;
  padding: ${({ $paddingX, $paddingY }) => `${$paddingY}px ${$paddingX}px`};
  line-height: 1.2;
  color: ${({ $color }) => $color};
  text-align: center;
  position: relative;
  > span {
    position: relative;
    z-index: 2;
  }
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background: ${({ $bg }) => ($bg ? $bg : 'transparent')};
    opacity: ${({ $opacity }) => ($opacity ? parseInt($opacity) / 100 : 1)};
  }
}
  
`;

const GenerateButton = styled(ActionButton)`
  width: 100%;
  height: 56px;
  font-weight: 700;
  &:disabled {
    background: ${({ theme }) => theme.colors.lightGray};
  }
`;

const DEFAULT_BACKGROUND_COLOR = '#ffffff';

const DEFAULT_FONT_COLOR = '#000000';

export const renderSubtitleImage = async (
  text: string,
  newLineSymbol = '\\N',
  maxWidth?: string,
) => {
  const el = document.createElement('div');
  el.classList.add('ml-subtitle-image');

  if (maxWidth) {
    el.style.maxWidth = maxWidth;
  }

  const span = document.createElement('span');

  span.innerHTML = text.replaceAll(newLineSymbol, '<br/>');

  el.append(span);

  document.body.appendChild(el);

  const imageDataUrl = await htmlToImage.toPng(el, {
    pixelRatio: 1,
  });

  document.body.removeChild(el);

  return imageDataUrl;
};

export interface FormDataT {
  styles: {
    font_size: string;
    padding_x: string;
    padding_y: string;
    font_color: string;
    background_color: string;
    background_opacity: string;
  };
}

interface Props {
  translation: MLProjectTranslation;
}

const SubtitlesStylesCard: FC<Props> = ({ translation }) => {
  const alerts = useAlerts();
  const toasts = useToasts();
  const queryClient = useQueryClient();
  const [formRecentlySubmitted, setFormRecentlySubmitted] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    setError,
    clearErrors,
    formState: { isSubmitting, isValid },
  } = useForm<FormDataT>({
    defaultValues: {
      styles: {
        padding_x: translation.subtitlesStyles?.paddingX ?? '15',
        padding_y: translation.subtitlesStyles?.paddingY ?? '10',
        font_size: translation.subtitlesStyles?.fontSize ?? '32',
        font_color:
          translation.subtitlesStyles?.fontColor ?? DEFAULT_FONT_COLOR,
        background_color: translation.subtitlesStyles?.backgroundColor ?? '',
        background_opacity:
          translation.subtitlesStyles?.backgroundOpacity ?? '100',
      },
    },
  });

  const submitHandler = handleSubmit(async (data) => {
    const toastId = toasts.create('loading');

    try {
      const subtitles = await Promise.all(
        translation
          .script!.filter((part) => part.applySubtitles)
          .map(async (part) => {
            return {
              uuid: part.uuid,
              image: await renderSubtitleImage(
                part.text[part.selectedTranslation ?? 'a'],
              ),
              ...(!!part.splittedPart && {
                splittedPart: await renderSubtitleImage(part.splittedPart.text),
              }),
            };
          }),
      );

      await api.put(`/v1/distribution/ml-translations/${translation.id}`, {
        subtitles_styles: data.styles,
        subtitles,
      });

      await queryClient.invalidateQueries([
        'ml_translations',
        {
          id: translation.id.toString(),
        },
      ]);

      reset(data);

      toasts.success(toastId, 'Saved');

      setFormRecentlySubmitted(true);
    } catch (err) {
      handleFormErrors<FormDataT>(err, setError, alerts, true);
      toasts.close(toastId);
    }
  });

  const onSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    clearErrors();
    submitHandler(e);
  };

  const form = watch();

  const backgroundColor = form.styles.background_color;

  const isSubmitDisabled =
    isSubmitting ||
    !isValid ||
    translation.status === MLProjectTranslationStatus.DONE ||
    translation.videoStatus === MLProjectTranslationVideoStatus.GENERATING ||
    !translation.script!.find((part) => part.applySubtitles) ||
    formRecentlySubmitted;

  const onDownloadSrt = () => {
    const arr = (translation.script ?? []).map((row) => {
      const timestamp = row.timestamp
        .split(' - ')
        .map((part, index) => '00:' + part + (!index ? ',001' : ',000'))
        .join(' --> ');

      return {
        text: row.text[row.selectedTranslation ?? 'a'],
        timestamp,
      };
    });

    srt.download(arr);
  };

  useDidUpdate(() => {
    setFormRecentlySubmitted(false);
  }, [translation.script]);

  return (
    <>
      <Card
        storageKey={`ml_translation_${translation.id}_subs_styles_card_visibility`}
        title="Apply subtitles styles"
        defaultIsOpened={false}
        subscribe
        description="Add subtitles and styles to suit your preferences and needs"
        actions={
          <ActionButton onClick={onSubmit} disabled={isSubmitDisabled}>
            Apply Subtitles
          </ActionButton>
        }
        hideActionsWhenCollapsed
        footer={
          <Footer>
            <Text $type="caption">Subtitles</Text>
            <ActionCard>
              <ActionCardContent>
                <Text $type="smallBold">Subtitles File (.srt)</Text>
                <Text $color="gray" $type="smallNormal">
                  Generate and download .srt subtitles file
                </Text>
              </ActionCardContent>
              <ActionButton $secondary onClick={onDownloadSrt}>
                Download
              </ActionButton>
            </ActionCard>
            <GenerateButton
              type="button"
              onClick={onSubmit}
              disabled={isSubmitDisabled}
            >
              Apply subtitles with selected settings
            </GenerateButton>
          </Footer>
        }
      >
        <Grid>
          <Text $type="caption">Preview Panel</Text>
          <PreviewBox>
            <SubtitlesBox>
              <div className="ml-subtitle-image">
                <span>
                  Lorem ipsum dolor sit
                  <br />
                  amet consectetur. Nunc.
                </span>
              </div>
            </SubtitlesBox>
          </PreviewBox>
          <Box>
            <ButtonsRow>
              <StyleButton
                $secondary={!!backgroundColor}
                onClick={() =>
                  setValue('styles.background_color', '', { shouldDirty: true })
                }
              >
                Original
              </StyleButton>
              <StyleButton
                $secondary={!backgroundColor}
                onClick={() =>
                  setValue(
                    'styles.background_color',
                    DEFAULT_BACKGROUND_COLOR,
                    { shouldDirty: true },
                  )
                }
              >
                In Background
              </StyleButton>
              <StyleButton disabled>Style 3</StyleButton>
              <StyleButton disabled>Style 4</StyleButton>
            </ButtonsRow>
          </Box>
          <Text $type="caption">Sizing</Text>
          <Box>
            <Row>
              <NumericInput<FormDataT>
                control={control}
                name="styles.padding_x"
                label="Horizontal Padding"
                inputLabel="px"
              />
              <NumericInput<FormDataT>
                control={control}
                name="styles.padding_y"
                label="Vertical Padding"
                inputLabel="px"
              />
            </Row>
          </Box>
          <Text $type="caption">Font & Color</Text>
          <Box>
            <Row>
              <ColorField<FormDataT>
                control={control}
                name="styles.font_color"
                label="Font Color"
                defaultValue={DEFAULT_FONT_COLOR}
              />
              <NumericInput<FormDataT>
                control={control}
                name="styles.font_size"
                label="Font Size"
                inputLabel="px"
              />
            </Row>
            {!!backgroundColor && (
              <BgRow>
                <ColorField<FormDataT>
                  control={control}
                  name="styles.background_color"
                  label="Subtitles Background Color"
                  defaultValue={DEFAULT_BACKGROUND_COLOR}
                />
                <NumericInput<FormDataT>
                  control={control}
                  name="styles.background_opacity"
                  label="Background Opacity"
                  inputLabel="%"
                />
              </BgRow>
            )}
          </Box>
        </Grid>
      </Card>
      <SubtitlesTextStyles
        $bg={form.styles.background_color}
        $opacity={form.styles.background_opacity}
        $color={form.styles.font_color}
        $paddingX={form.styles.padding_x}
        $paddingY={form.styles.padding_y}
        $fontSize={form.styles.font_size}
      />
    </>
  );
};

export default SubtitlesStylesCard;
